import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatKoreanPrice, AREA_LABEL } from '@dongtan/shared';
import {
  getHomeDashboard,
  type HomeDashboardData,
  type AIPredictionItem,
  type TrendingComplexItem,
} from '../services/dashboardService';

/* ─── 정적 데이터 ─── */
const FEATURES = [
  {
    icon: '🔍', title: '매물 검색', link: '/properties',
    color: 'from-blue-500 to-blue-600',
    desc: '다양한 조건으로 검색하고 지도에서 한눈에 확인',
    details: ['지도/목록/분할 뷰', '실거래가 이력', '유사 매물 추천'],
  },
  {
    icon: '📊', title: '시장 분석', link: '/analysis/market',
    color: 'from-emerald-500 to-emerald-600',
    desc: '가격 추이, 거래량, 수급 지표를 실시간 분석',
    details: ['가격 추이 차트', '거래량 히트맵', '지역별 비교'],
  },
  {
    icon: '🤖', title: 'AI 분석 엔진', link: '/engines',
    color: 'from-purple-500 to-purple-600',
    desc: '5대 핵심 엔진이 매물의 숨겨진 가치를 분석',
    details: ['재무 분석', '출퇴근 시뮬', '일조/조망/소음', '인프라 스코어링', '엑시트 전략'],
  },
  {
    icon: '💰', title: '투자 시뮬레이션', link: '/simulation',
    color: 'from-amber-500 to-amber-600',
    desc: 'ROI, NPV, IRR 시뮬레이션 및 대출 상환 비교',
    details: ['수익률 분석', '대출 상환 3방식 비교', '시나리오별 비교'],
  },
  {
    icon: '💬', title: 'AI 상담', link: '/consultation',
    color: 'from-cyan-500 to-cyan-600',
    desc: 'AI가 맞춤 조건을 파악하고 최적 매물 추천',
    details: ['대화형 니즈 파악', '맞춤 매물 매칭', '투자 전략 제안'],
  },
  {
    icon: '🗺️', title: '동탄 정보', link: '/dongtan',
    color: 'from-sky-500 to-sky-600',
    desc: '개발 계획, 인프라 현황, 권역별 특성',
    details: ['권역별 개요', '개발 계획 타임라인', '인프라 지도'],
  },
  {
    icon: '🧮', title: '부동산 계산기', link: '/tools/calculators',
    color: 'from-teal-500 to-teal-600',
    desc: '대출 상환, 취득세, 전월세 전환을 간편 계산',
    details: ['대출 상환 3방식 비교', '취득세/보유세 자동 계산', '전세↔월세 전환'],
  },
];

const ENGINES = [
  { title: 'Financial Engine', subtitle: '자산 맞춤형 금융 퀀트', desc: '가용 자본, LTV/DSR, 세금, 현금흐름 최적화', color: 'bg-blue-50 text-blue-700 border-blue-200', link: '/engines/financial' },
  { title: 'Urban Mobility', subtitle: '교통/도시계획 예측', desc: 'GTX/철도 분석, 출퇴근 시뮬, 프리미엄 시점 예측', color: 'bg-green-50 text-green-700 border-green-200', link: '/engines' },
  { title: 'Spatial Engine', subtitle: '3D 환경/공간 분석', desc: '일조량, 조망 시뮬, 소음/프라이버시 분석', color: 'bg-purple-50 text-purple-700 border-purple-200', link: '/engines' },
  { title: 'Infrastructure Index', subtitle: '하이퍼-로컬 인프라', desc: '학원가/학교/야간 소아과/수변 산책로', color: 'bg-orange-50 text-orange-700 border-orange-200', link: '/engines' },
  { title: 'Value-up & Exit', subtitle: '가치상승/엑시트 전략', desc: '하락장 방어력, 리모델링 ROI, 최적 매도 시점', color: 'bg-red-50 text-red-700 border-red-200', link: '/engines' },
];

/* ─── 유틸 ─── */
function fmtManwon(n: number): string {
  if (n >= 10000) {
    const uk = Math.floor(n / 10000);
    const man = n % 10000;
    return man > 0 ? `${uk}억 ${man.toLocaleString()}만` : `${uk}억`;
  }
  return `${n.toLocaleString()}만`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

export default function HomePage() {
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getHomeDashboard()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 py-20 text-white">
        <div className="container-app text-center">
          <h1 className="mb-4 text-4xl font-extrabold leading-snug md:text-5xl md:leading-relaxed lg:text-6xl lg:leading-relaxed">
            집을 <span className="text-yellow-300">사는 것</span>이 아니라,
            <br />
            <span className="text-rose-300">미래 자산 전략</span>을 <span className="text-yellow-300">설계</span>하다
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-primary-100">
            <span className="font-semibold text-white">동탄 지역</span> AI 기반 부동산 초정밀 가치 분석 플랫폼.
            <br className="hidden sm:block" />
            <span className="font-semibold text-white">5대 핵심 분석 엔진</span>으로 데이터 기반 의사결정을 지원합니다.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/properties" className="rounded-xl bg-white px-8 py-3 text-lg font-semibold text-primary-700 shadow-lg transition hover:bg-primary-50">
              매물 검색 시작
            </Link>
            <Link to="/engines" className="rounded-xl border-2 border-white/30 px-8 py-3 text-lg font-semibold text-white transition hover:bg-white/10">
              AI 분석 엔진
            </Link>
          </div>

          {/* AI 챗봇 입력창 */}
          <div className="mx-auto mt-10 max-w-2xl">
            <div className="flex items-center gap-2 rounded-full bg-white px-5 py-3 shadow-lg transition-all focus-within:ring-2 focus-within:ring-primary-300 dark:bg-gray-800">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    navigate(`/ai-chat?q=${encodeURIComponent(chatInput.trim())}`);
                  }
                }}
                placeholder="동탄 부동산에 대해 질문하세요..."
                className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 outline-none dark:text-gray-100 dark:placeholder-gray-500"
              />
              <button
                onClick={() => {
                  if (chatInput.trim()) {
                    navigate(`/ai-chat?q=${encodeURIComponent(chatInput.trim())}`);
                  }
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white transition hover:bg-primary-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-xs text-white/40">AI 답변은 참고용이며, 투자 결정의 책임은 본인에게 있습니다.</p>
          </div>
        </div>
      </section>

      {/* ─── AI 미래가격 예측 + 인기 단지 킬러 섹션 ─── */}
      {!loading && (
        <section className="relative overflow-hidden border-b border-gray-200 bg-gradient-to-br from-gray-50 via-white to-gray-50 py-14 dark:border-gray-800 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          {/* 배경 장식 */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary-500/5 blur-3xl dark:bg-primary-500/10" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-rose-500/5 blur-3xl dark:bg-rose-500/10" />
          </div>

          <div className="container-app relative z-10">
            {/* AI 미래가격 예측 TOP */}
            <div className="mb-10">
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-lg text-white shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">AI 미래가격 예측 TOP</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">1년 후 예측 상승률 기준 · AI 분석 엔진</p>
                </div>
                <Link to="/analysis/appreciation" className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-400 dark:hover:text-white">
                  전체 보기
                </Link>
              </div>

              {data?.aiPredictions && data.aiPredictions.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {data.aiPredictions.map((item, idx) => (
                    <AIPredictionCard key={item.propertyId} item={item} rank={idx + 1} />
                  ))}
                </div>
              ) : (
                <AIAnalysisLoadingCard progress={data?.aiAnalysisProgress} />
              )}
            </div>

            {/* 지금 가장 뜨는 단지 TOP */}
            <div>
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-red-500 text-lg text-white shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 0 0 .495-7.468 5.99 5.99 0 0 0-1.925 3.547 5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">지금 가장 뜨는 단지</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">최근 3개월 거래량 + 가격상승률 기준</p>
                </div>
                <Link to="/investment-ranking" className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-400 dark:hover:text-white">
                  전체 보기
                </Link>
              </div>

              {data?.trendingComplexes && data.trendingComplexes.length > 0 ? (
                <div className="space-y-3">
                  {data.trendingComplexes.map((item, idx) => (
                    <TrendingComplexRow key={item.propertyId} item={item} rank={idx + 1} maxHeat={data.trendingComplexes[0]?.heatScore || 100} />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-800/50">
                  <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-300">거래 데이터를 분석 중입니다</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">충분한 거래가 쌓이면 인기 단지 랭킹이 표시됩니다</p>
                  <Link to="/analysis/market" className="mt-4 inline-block rounded-lg bg-gradient-to-r from-amber-500 to-red-500 px-5 py-2 text-sm font-semibold text-white shadow transition hover:shadow-lg">
                    시장 분석 보기
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─── 라이브 KPI ─── */}
      <section className="border-b bg-white py-10">
        <div className="container-app">
          {loading ? (
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="text-center">
                  <div className="mx-auto h-10 w-20 animate-pulse rounded bg-gray-100" />
                  <div className="mx-auto mt-2 h-4 w-24 animate-pulse rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : data ? (
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              <KPICard value={`${data.counts.totalProperties}`} label="분석 대상 단지" />
              <KPICard
                value={`${data.counts.activeListings}`}
                label="활성 매물"
                sub="네이버 부동산 기준"
              />
              <KPICard
                value={data.marketKPI.avgPrice > 0 ? fmtManwon(data.marketKPI.avgPrice) : '-'}
                label="평균 거래가"
                sub={data.marketKPI.period}
                change={data.marketKPI.avgPriceChange}
              />
              <KPICard
                value={`${data.counts.totalTransactions.toLocaleString()}`}
                label="누적 실거래"
                sub="건"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              <KPICard value="15+" label="분석 대상 단지" />
              <KPICard value="5" label="AI 분석 엔진" />
              <KPICard value="40+" label="실거래 데이터" />
              <KPICard value="24/7" label="AI 상담 가능" />
            </div>
          )}
        </div>
      </section>

      {/* ─── 시장 트렌드 + 저평가 + 가격 변동 ─── */}
      {data && (
        <section className="bg-gray-50 py-12">
          <div className="container-app">
            <div className="grid gap-6 lg:grid-cols-3">
              {/* 최근 가격 변동 */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">최근 가격 변동</h3>
                  <Link to="/notifications" className="text-xs font-medium text-primary-600 hover:underline">
                    전체 보기
                  </Link>
                </div>
                {data.recentPriceChanges.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-400">아직 가격 변동이 없습니다</p>
                ) : (
                  <div className="space-y-3">
                    {data.recentPriceChanges.map((pc) => {
                      const isDown = pc.priceDelta < 0;
                      return (
                        <Link
                          key={pc.id}
                          to={pc.listingId ? `/naver/listings/${pc.listingId}` : `/properties/${pc.propertyId}`}
                          className="flex items-center gap-3 rounded-lg p-2 transition hover:bg-gray-50"
                        >
                          <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                            isDown ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {isDown ? '↓' : '↑'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-900">{pc.complexName}</p>
                            <p className="text-xs text-gray-400">
                              {fmtManwon(pc.oldPrice)} → {fmtManwon(pc.newPrice)}
                              <span className={isDown ? ' text-blue-600' : ' text-red-600'}>
                                {' '}({pc.changePercent > 0 ? '+' : ''}{pc.changePercent}%)
                              </span>
                            </p>
                          </div>
                          <span className="flex-shrink-0 text-[10px] text-gray-400">{timeAgo(pc.detectedAt)}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 저평가 매물 TOP */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">저평가 매물 TOP</h3>
                  <Link to="/properties/undervalued" className="text-xs font-medium text-red-600 hover:underline">
                    전체 보기
                  </Link>
                </div>
                {data.undervalued.topItems.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-400">저평가 매물이 없습니다</p>
                ) : (
                  <div className="space-y-3">
                    {data.undervalued.topItems.map((item, idx) => (
                      <Link
                        key={item.listingId}
                        to={`/naver/listings/${item.listingId}`}
                        className="flex items-center gap-3 rounded-lg p-2 transition hover:bg-gray-50"
                      >
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-700">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">{item.complexName}</p>
                          <p className="text-xs text-gray-400">
                            호가 {fmtManwon(item.askingPrice)} · 기준가 {fmtManwon(item.referencePrice)}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-red-600">-{item.discount.toFixed(1)}%</span>
                          <p className="text-[10px] text-gray-400">score {item.score}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                {data.undervalued.stats && (
                  <div className="mt-4 flex items-center gap-4 rounded-lg bg-red-50 px-4 py-2.5">
                    <div className="text-center">
                      <p className="text-lg font-bold text-red-700">{data.undervalued.stats.totalFound}</p>
                      <p className="text-[10px] text-red-500">발견</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-red-700">-{data.undervalued.stats.avgDiscount.toFixed(1)}%</p>
                      <p className="text-[10px] text-red-500">평균 할인</p>
                    </div>
                    {data.undervalued.stats.topDiscount && (
                      <div className="text-center">
                        <p className="text-lg font-bold text-red-700">-{data.undervalued.stats.topDiscount.discount.toFixed(1)}%</p>
                        <p className="text-[10px] text-red-500">최대 할인</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 최근 실거래 */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">최근 실거래</h3>
                  <div className="flex items-center gap-3">
                    <Link to="/analysis/market" className="text-xs font-medium text-primary-600 hover:underline">
                      시장분석
                    </Link>
                    <span className="text-gray-300">|</span>
                    <Link to="/analysis/transactions" className="text-xs font-medium text-primary-600 hover:underline">
                      전체 보기
                    </Link>
                  </div>
                </div>
                {data.recentTransactions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-400">실거래 데이터가 없습니다</p>
                ) : (
                  <div className="space-y-3">
                    {data.recentTransactions.map((tx) => (
                      <Link
                        key={tx.id}
                        to={`/properties/${tx.property.id}`}
                        className="flex items-center gap-3 rounded-lg p-2 transition hover:bg-gray-50"
                      >
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                          {tx.dealMonth}/{tx.dealDay}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">{tx.property.complexName}</p>
                          <p className="text-xs text-gray-400">
                            {tx.exclusiveArea}㎡ · {tx.floor}층
                            {tx.property.dongtanArea && ` · ${AREA_LABEL[tx.property.dongtanArea] || tx.property.dongtanArea}`}
                          </p>
                        </div>
                        <span className="flex-shrink-0 text-sm font-bold text-primary-700">
                          {formatKoreanPrice(tx.dealAmount)}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 권역별 시장 현황 */}
            {Object.keys(data.areaBreakdown).length > 0 && (
              <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-bold text-gray-900">권역별 시장 현황</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {Object.entries(data.areaBreakdown).map(([area, info]) => (
                    <Link
                      key={area}
                      to={`/analysis/area/${area}`}
                      className="rounded-xl border border-gray-200 p-4 transition hover:border-primary-300 hover:shadow-sm"
                    >
                      <p className="text-sm font-semibold text-gray-900">{AREA_LABEL[area] || area}</p>
                      <p className="mt-1 text-lg font-bold text-primary-700">{fmtManwon(info.avgPrice)}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-gray-400">거래 {info.count}건</span>
                        {info.change !== 0 && (
                          <span className={`text-xs font-medium ${info.change > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                            {info.change > 0 ? '+' : ''}{info.change.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── 플랫폼 주요 기능 ─── */}
      <section className={data ? 'py-16' : 'bg-gray-50 py-20'}>
        <div className="container-app">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-bold text-gray-900">플랫폼 주요 기능</h2>
            <p className="mx-auto max-w-xl text-gray-500">
              부동산 검색부터 AI 분석, 투자 시뮬레이션, 커뮤니티까지 — 동탄 부동산의 모든 것을 한 곳에서
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <Link
                key={f.title}
                to={f.link}
                className="group flex flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-lg hover:-translate-y-1"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${f.color} text-2xl text-white shadow-sm`}>
                    {f.icon}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 transition group-hover:text-primary-600">
                    {f.title}
                  </h3>
                </div>
                <p className="mb-4 text-sm leading-relaxed text-gray-500">{f.desc}</p>
                <ul className="mt-auto space-y-1.5">
                  {f.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-xs text-gray-400">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-400" />
                      {d}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 5대 핵심 엔진 ─── */}
      <section className="bg-gray-50 py-16">
        <div className="container-app">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-bold">5대 핵심 분석 엔진</h2>
            <p className="mx-auto max-w-xl text-gray-500">
              데이터로 미래 가치를 설계하는 AI 자산 전략가 플랫폼
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {ENGINES.map((e) => (
              <Link
                key={e.title}
                to={e.link}
                className="group rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md"
              >
                <span className={`inline-block rounded-lg border px-3 py-1 text-xs font-semibold ${e.color}`}>
                  {e.subtitle}
                </span>
                <h3 className="mt-3 text-xl font-bold text-gray-900 group-hover:text-primary-600">
                  {e.title}
                </h3>
                <p className="mt-2 text-sm text-gray-500">{e.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 이용 흐름 ─── */}
      <section className="bg-primary-50 py-16">
        <div className="container-app">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-bold text-gray-900">이렇게 이용하세요</h2>
            <p className="text-gray-500">4단계로 최적의 부동산 의사결정을 내릴 수 있습니다</p>
          </div>
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { step: '01', title: '매물 탐색', desc: '조건에 맞는 매물을 검색하고 지도에서 위치를 확인하세요.' },
              { step: '02', title: 'AI 분석', desc: '5대 엔진이 금융, 교통, 환경, 인프라, 투자가치를 분석합니다.' },
              { step: '03', title: '시뮬레이션', desc: '투자 수익률과 대출 상환을 시뮬레이션합니다.' },
              { step: '04', title: '의사결정', desc: '리포트를 받아보고, 커뮤니티와 중개사를 통해 실행하세요.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-xl font-bold text-white shadow">
                  {item.step}
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-900">{item.title}</h3>
                <p className="text-sm leading-relaxed text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 py-16 text-white">
        <div className="container-app text-center">
          <h2 className="mb-4 text-3xl font-bold">지금 바로 시작하세요</h2>
          <p className="mx-auto mb-8 max-w-lg text-primary-200">
            회원가입 후 AI 상담을 통해 나에게 딱 맞는 동탄 매물을 찾아보세요.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/register" className="rounded-xl bg-white px-8 py-3 text-lg font-semibold text-primary-700 shadow-lg transition hover:bg-primary-50">
              무료 회원가입
            </Link>
            <Link to="/consultation" className="rounded-xl border-2 border-white/30 px-8 py-3 text-lg font-semibold text-white transition hover:bg-white/10">
              AI 상담 시작
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── KPI Card 컴포넌트 ─── */
function KPICard({ value, label, sub, change }: {
  value: string;
  label: string;
  sub?: string;
  change?: number;
}) {
  return (
    <div className="text-center">
      <div className="text-3xl font-extrabold text-primary-600 md:text-4xl">{value}</div>
      <div className="mt-1 text-sm text-gray-500">{label}</div>
      {(sub || change !== undefined) && (
        <div className="mt-0.5 flex items-center justify-center gap-1.5">
          {sub && <span className="text-xs text-gray-400">{sub}</span>}
          {change !== undefined && change !== 0 && (
            <span className={`text-xs font-medium ${change > 0 ? 'text-red-600' : 'text-blue-600'}`}>
              {change > 0 ? '▲' : '▼'}{Math.abs(change).toFixed(1)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── AI 분석 로딩 카드 (배치 미완료 시 애니메이션) ─── */
function AIAnalysisLoadingCard({ progress }: { progress: { analyzed: number; total: number } | null | undefined }) {
  const [animPct, setAnimPct] = useState(0);

  // 실제 진행률이 있으면 그걸 쓰고, 없으면 애니메이션
  const realPct = progress && progress.total > 0
    ? Math.round((progress.analyzed / progress.total) * 100)
    : null;

  useEffect(() => {
    if (realPct !== null) {
      setAnimPct(realPct);
      return;
    }
    // 데이터 없을 때: 빠르게 올라가다 느려지는 애니메이션 (0→92%)
    let frame: number;
    let current = 0;
    const tick = () => {
      current += (92 - current) * 0.02;
      setAnimPct(Math.round(current));
      if (current < 91) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [realPct]);

  const steps = [
    { label: '시장 데이터 수집', done: animPct > 20 },
    { label: 'ML 가격 예측 모델 실행', done: animPct > 50 },
    { label: '미래 가치 산출', done: animPct > 80 },
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white/80 p-8 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="flex flex-col items-center text-center">
        {/* 아이콘 - pulse 애니메이션 */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-rose-500/10 to-orange-500/10 dark:from-rose-500/20 dark:to-orange-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7 animate-pulse text-rose-500 dark:text-rose-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
          </svg>
        </div>

        <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          AI 분석 엔진이 데이터를 분석하고 있습니다
        </p>
        {progress && progress.total > 0 && (
          <p className="mb-1 text-xs text-gray-400 dark:text-gray-500">
            전체 {progress.total}개 단지 중{' '}
            <span className="font-semibold text-rose-500 dark:text-rose-400">{progress.analyzed}개</span> 완료
          </p>
        )}
        <p className="mb-5 text-xs text-gray-400 dark:text-gray-500">5대 핵심 엔진이 동탄 전 단지의 미래 가치를 분석합니다</p>

        {/* 프로그레스 바 */}
        <div className="mb-2 w-full max-w-sm">
          <div className="h-2.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-rose-500 to-orange-500 transition-all duration-300 ease-out"
              style={{ width: `${animPct}%` }}
            />
          </div>
          <div className="mt-1.5 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>분석 진행 중...</span>
            <span className="font-medium text-rose-500 dark:text-rose-400">{animPct}%</span>
          </div>
        </div>

        {/* 단계 표시 */}
        <div className="mb-5 mt-3 flex justify-center gap-6 text-xs text-gray-400 dark:text-gray-500">
          {steps.map((s) => (
            <span key={s.label} className={s.done ? 'text-emerald-500 dark:text-emerald-400' : ''}>
              {s.done ? '✓ ' : '· '}{s.label}
            </span>
          ))}
        </div>

        <Link to="/engines" className="inline-block rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white shadow transition hover:shadow-lg">
          AI 엔진 알아보기
        </Link>
      </div>
    </div>
  );
}

/* ─── AI 미래가격 예측 카드 ─── */
function AIPredictionCard({ item, rank }: { item: AIPredictionItem; rank: number }) {
  const isUp = item.changeRate > 0;
  const rankColors = ['from-yellow-400 to-amber-500', 'from-gray-300 to-gray-400', 'from-amber-600 to-amber-700'];
  const rankBg = rankColors[rank - 1] || 'from-gray-500 to-gray-600';

  return (
    <Link
      to={`/properties/${item.propertyId}`}
      className="group relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800/80 dark:shadow-none dark:hover:border-gray-500 dark:hover:bg-gray-800"
    >
      {/* 순위 배지 */}
      <span className={`absolute -left-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br ${rankBg} text-sm font-bold text-white shadow-lg`}>
        {rank}
      </span>

      {/* 단지 정보 */}
      <div className="mb-3 mt-1">
        <p className="truncate text-sm font-bold text-gray-900 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-300">{item.complexName}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {item.dongtanArea ? (AREA_LABEL[item.dongtanArea] || item.dongtanArea) : '동탄'}
          {item.totalUnits ? ` · ${item.totalUnits.toLocaleString()}세대` : ''}
        </p>
      </div>

      {/* 가격 변화 */}
      <div className="mb-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">현재</span>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{fmtManwon(item.currentPrice)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">1년 후</span>
          <span className="text-sm font-bold text-gray-900 dark:text-white">{fmtManwon(item.predictedPrice)}</span>
        </div>
      </div>

      {/* 상승률 + 신뢰도 */}
      <div className="flex items-center justify-between">
        <span className={`rounded-lg px-2.5 py-1 text-lg font-extrabold ${isUp ? 'bg-rose-500/10 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400' : 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'}`}>
          {isUp ? '+' : ''}{item.changeRate.toFixed(1)}%
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          신뢰도 {(item.confidence * 100).toFixed(0)}%
        </span>
      </div>
    </Link>
  );
}

/* ─── 인기 단지 랭킹 Row ─── */
function TrendingComplexRow({ item, rank, maxHeat }: { item: TrendingComplexItem; rank: number; maxHeat: number }) {
  const heatPercent = maxHeat > 0 ? Math.round((item.heatScore / maxHeat) * 100) : 0;
  const isUp = item.priceChange > 0;
  const rankEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';

  return (
    <Link
      to={`/properties/${item.propertyId}`}
      className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm transition hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800/60 dark:shadow-none dark:hover:border-gray-500 dark:hover:bg-gray-800"
    >
      {/* 순위 */}
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-lg font-bold text-gray-800 dark:bg-gray-700 dark:text-white">
        {rankEmoji || rank}
      </span>

      {/* 단지 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-bold text-gray-900 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-300">{item.complexName}</p>
          <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            {item.dongtanArea ? (AREA_LABEL[item.dongtanArea] || item.dongtanArea) : '동탄'}
          </span>
        </div>
        {/* 히트 게이지 바 */}
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-700"
            style={{ width: `${heatPercent}%` }}
          />
        </div>
      </div>

      {/* 거래 정보 */}
      <div className="flex flex-shrink-0 items-center gap-6 text-right">
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500">평균가</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtManwon(item.avgPrice)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500">거래</p>
          <p className="text-sm font-semibold text-amber-500 dark:text-amber-400">{item.transactionCount}건</p>
        </div>
        <div className="w-16">
          <p className="text-xs text-gray-400 dark:text-gray-500">변동률</p>
          <p className={`text-sm font-bold ${isUp ? 'text-rose-500 dark:text-rose-400' : item.priceChange < 0 ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400'}`}>
            {isUp ? '+' : ''}{item.priceChange.toFixed(1)}%
          </p>
        </div>
      </div>
    </Link>
  );
}
